import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

// Live provider status for Stripe, Certn, Persona, Google OAuth and friends.
// Fetched once per session; every gated button reads from here so the UI is
// honest about what is actually connected instead of pretending.

const DEFAULTS = {
  persona: { configured: false },
  certn: { configured: false },
  stripe: { configured: false },
  smtp: { configured: false },
  google_oauth: { configured: false },
  google_places: { configured: false },
  vapi: { configured: false },
  ddf: { configured: false },
  openai: { configured: false },
};

export const PROVIDER_LABELS = {
  stripe: "Stripe",
  certn: "Certn",
  persona: "Persona",
  google_oauth: "Google",
  smtp: "SMTP",
  vapi: "Vapi",
  ddf: "CREA DDF",
  openai: "OpenAI",
};

const IntegrationsContext = createContext({ status: DEFAULTS, loaded: false });

export function IntegrationsProvider({ children }) {
  const [state, setState] = useState({ status: DEFAULTS, loaded: false });

  useEffect(() => {
    let alive = true;
    api
      .get("/integrations/status")
      .then((res) => {
        if (!alive || !res.data || typeof res.data !== "object") return;
        setState({ status: { ...DEFAULTS, ...res.data }, loaded: true });
      })
      .catch(() => {
        // Offline or endpoint missing — everything stays "not configured",
        // which is the safe, honest default for payments and screening.
        if (alive) setState({ status: DEFAULTS, loaded: true });
      });
    return () => {
      alive = false;
    };
  }, []);

  return <IntegrationsContext.Provider value={state}>{children}</IntegrationsContext.Provider>;
}

export function useIntegrations() {
  return useContext(IntegrationsContext);
}

// True when a provider has keys and its features can go live.
export function providerReady(status, provider) {
  return !!status?.[provider]?.configured;
}

// Backend answers 503 {"code":"INTEGRATION_NOT_CONFIGURED","provider":...}
// when a key is missing. That is a quiet state, never an error toast.
export function isNotConfigured(error) {
  return error?.response?.status === 503 && error.response.data?.code === "INTEGRATION_NOT_CONFIGURED";
}

// 409 {"code":"CONSENT_REQUIRED"} — the renter has not shared their profile.
export function isConsentRequired(error) {
  return error?.response?.status === 409 && error.response.data?.code === "CONSENT_REQUIRED";
}
